import { FC, forwardRef, MouseEventHandler, useContext, useEffect, useState } from 'react';
import ReactDatePicker, {
	ReactDatePickerCustomHeaderProps,
} from 'react-datepicker';
import { da } from 'date-fns/locale';
import './DatePickerInput.scss';
import { DateContext } from '../../Contexts/DateContext';
import { Icon } from '../Icon/Icon';
import { format } from 'date-fns';

type Props = {
	defaultValue: string;
	inputChanged: (date: Date) => void;
};

export const DatePickerInput: FC<Props> = ({ defaultValue, inputChanged}) => {
 	//Get current date context
	const [[startDate, endDate], setSelectedDate] = useContext(DateContext);
	
	const isPlaceholder = defaultValue === 'Vælg dato';
	const parsedDefaultDate = new Date(defaultValue);
	const hasValidDefaultDate = !isPlaceholder && !Number.isNaN(parsedDefaultDate.getTime());
	const defaultDate = hasValidDefaultDate ? parsedDefaultDate : null;
	const [inputDate , setInputDate] = useState<Date | null>(defaultDate);
	
	useEffect(() => {
		if (hasValidDefaultDate) {
			setInputDate(parsedDefaultDate);
			return;
		}

		// When creating a new order we intentionally show the placeholder.
		// In that state, keep the datepicker "unselected" so clicking today's date is a change event.
		setInputDate(null);
	}, [defaultValue]);

	function handleSetDate(date: Date | [Date | null, Date | null] | null) {
		if (date instanceof Date) {
			inputChanged(date);
			setInputDate(date);
		}
	}

	const handleOnBlur = () => {
	};

	let isSelectedDate = hasValidDefaultDate || (inputDate instanceof Date); 

	const selectedDate = isSelectedDate && inputDate instanceof Date ? format(inputDate, 'dd/MM/yyyy') : 'Vælg dato';
	const InputElement = <Input selectedDate={selectedDate}/>

	return (
		<>
			<ReactDatePicker
				portalId="datepicker-portal"
				className="react-datepicker"
				calendarClassName={isSelectedDate ? 'DatePicker--hasSelection' : 'DatePicker--noSelection'}
				selected={isSelectedDate ? inputDate : null}
				openToDate={inputDate ?? startDate}
				startDate={null}
				endDate={null}
				onChange={handleSetDate}
				onCalendarClose={handleOnBlur}
				locale={da}
				customInput={InputElement}
				showWeekNumbers={true}
				renderCustomHeader={DatePickerHeader}
				selectsRange={false}
			/>
		</>
	);
};

type ButtonProps = {
	onClick?: MouseEventHandler<HTMLButtonElement>;
	selectedDate: string | undefined;
};


const Input = forwardRef<HTMLButtonElement, ButtonProps>(
	(props, ref) => (
		<button
			className='DatePickerInput'
			onClick={props.onClick}
		>
			{
				props.selectedDate && 
				props.selectedDate
			}
			<Icon name='Carret' width={12} />
		</button>
	)
);

const DatePickerHeader: FC<ReactDatePickerCustomHeaderProps> = (props) => (
	<div className="DatePickerHeader">
		<button
			onClick={props.decreaseMonth}
			className="DatePickerHeader__button DatePickerHeader__button--prev"
		>
			<Icon name="CaretLeftGold" width={9} />
		</button>

		<h1 className="DatePickerHeader__title">{`${
			MONTHS[props.date.getMonth()]
		} ${props.date.getFullYear()}`}</h1>

		<button
			onClick={props.increaseMonth}
			className="DatePickerHeader__button DatePickerHeader__button--next"
		>
			<Icon name="CaretLeftGold" width={9} />
		</button>
	</div>
);

const MONTHS = [
	'Januar',
	'Februar',
	'Marts',
	'April',
	'Maj',
	'Juni',
	'Juli',
	'August',
	'September',
	'Oktober',
	'November',
	'December',
];
